import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';
import { Outlet, generatePath, useOutletContext } from 'react-router';

import { ContentWithStickyHeader } from '@sorare/core/src/atoms/layout/ContentWithStickyHeader';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import { Tabs, TeamPageTabs } from '@sorare/core/src/components/seo/Team/Tabs';
import {
  FOOTBALL_CLUBS_SLUG,
  FOOTBALL_CLUBS_SLUG_CARDS,
  FOOTBALL_CLUBS_SLUG_FIXTURES,
  FOOTBALL_CLUBS_SLUG_STANDINGS,
  FOOTBALL_CLUBS_SLUG_TEAM,
} from '@sorare/core/src/constants/__generated__/routes';
import { NoIndex } from '@sorare/core/src/contexts/seo';
import { useQueryLoader } from '@sorare/core/src/hooks/graphql/useQueryLoader';
import { queryLoader } from '@sorare/core/src/lib/navigation/queryLoader';

import type {
  FootballSeoClubQuery,
  FootballSeoClubQueryVariables,
} from './__generated__/layout.graphql';
import type { PageParams } from './__generated__/routeParams';

const FOOTBALL_SEO_CLUB_QUERY = gql`
  query FootballSeoClubQuery($slug: String!) {
    football {
      club(slug: $slug) {
        slug
        name
        domesticLeague {
          slug
          seasons {
            id
            startYear
          }
        }
        latestGames(first: 1) {
          nodes {
            id
          }
        }
      }
    }
  }
` as TypedDocumentNode<FootballSeoClubQuery, FootballSeoClubQueryVariables>;

type LayoutData = FootballSeoClubQuery['football']['club'];

export const useLayoutData = () => {
  return useOutletContext<LayoutData>();
};

export const loader = queryLoader(
  FOOTBALL_SEO_CLUB_QUERY,
  ({ slug }: PageParams) => ({
    slug,
  })
);

const ClubLayout = () => {
  const { data } = useQueryLoader<FootballSeoClubQuery>();

  const { club } = data.football;
  const slug = club?.slug;

  const tabs: Record<TeamPageTabs, string> = useMemo(() => {
    if (!slug) return {} as Record<TeamPageTabs, string>;

    return {
      [TeamPageTabs.OVERVIEW]: generatePath(FOOTBALL_CLUBS_SLUG, {
        slug,
      }),
      [TeamPageTabs.STANDINGS]: generatePath(FOOTBALL_CLUBS_SLUG_STANDINGS, {
        slug,
      }),
      [TeamPageTabs.FIXTURES]: generatePath(FOOTBALL_CLUBS_SLUG_FIXTURES, {
        slug,
      }),
      [TeamPageTabs.TEAM]: generatePath(FOOTBALL_CLUBS_SLUG_TEAM, {
        slug,
      }),
      [TeamPageTabs.CARDS]: generatePath(FOOTBALL_CLUBS_SLUG_CARDS, {
        slug,
      }),
    };
  }, [slug]);

  if (!slug || !club) return null;

  const hasEnoughSEOData =
    !!club.domesticLeague || club.latestGames.nodes.length > 0;

  return (
    <>
      <PageHeader text={club.name} />
      {!hasEnoughSEOData && <NoIndex />}
      <ContentWithStickyHeader header={<Tabs tabs={tabs} />}>
        <Outlet context={club satisfies LayoutData} />
      </ContentWithStickyHeader>
    </>
  );
};

export default ClubLayout;
